import React, { useState, useEffect, type ChangeEvent, type MouseEvent } from "react";
import { Box, Chip } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "@mui/material/Button";
import { getUser, createUser, updateUser } from "../../../../api";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../../components/SnackBarCustom";
import type { UserDetail } from "../../../../types/user";

type AccountEditUser = UserDetail & {
  name?: string;
  m_organizations?: Array<{ department_name?: string }>;
};

export default function AccountEdit() {
  const navigateback = useNavigate();
  const handleGoBack = () => {
    navigateback(-1);
  };
  const searchParams = new URLSearchParams(useLocation().search);
  const userId = searchParams.get("user-id");

  const [user, setUser] = useState<AccountEditUser>({});
  const [showPassword, setShowPassword] = useState(false);
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchUser = async () => {
    if (!userId) {
      return;
    }
    try {
      const response = await getUser(userId);
      if (response.status === "ok") {
        setUser(response.user as AccountEditUser);
        setSnackBarstatus(response);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    void fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleChangePass = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setUser((prevValue) => ({
      ...prevValue,
      password: newValue,
    }));
  };

  const putPostUser = async (isSuccess: boolean) => {
    if (!isSuccess) {
      console.log("何も行いません");
      return;
    }

    const params = {
      password: user.password,
    };
    try {
      const status = userId
        ? await updateUser(userId, params)
        : await createUser(params);
      setSnackBarstatus(status);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
        }}
      >
        <Box
          onClick={handleGoBack}
          sx={{
            display: "flex",
            height: "48px",
            alignItems: "center",
            paddingLeft: "40px",
            fontSize: 20,
            fontWeight: "bold",
            whiteSpace: "nowrap",
            color: "primary.main",
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": {
              color: "primary.dark",
            },
            "&:active": {
              color: "primary.light",
            },
          }}
        >
          アカウント一覧
        </Box>
        <Box
          sx={{
            display: "flex",
            height: "48px",
            alignItems: "center",
            fontSize: 20,
            fontWeight: "bold",
            whiteSpace: "nowrap",
          }}
        >
          /パスワード更新
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          justifyContent: "center",
          borderRadius: 2,
          borderColor: "primary.main",
          boxShadow: 3,
          color: "outline.dark",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            justifyContent: "center",
            gap: "16px",
            paddingTop: "32px",
            paddingRight: "40px",
            paddingLeft: "40px",
            paddingBottom: "32px",
            "@media screen and (max-width:900px)": {
              padding: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "min-content",
              paddingLeft: "40px",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              "@media screen and (max-width:900px)": {
                padding: "0px",
              },
            }}
          >
            ユーザ情報
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "min-content",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              "@media screen and (max-width:600px)": {
                width: "100%",
                flexDirection: "column",
              },
            }}
          >
            <Box sx={{ width: "248px" }}>組織名（変更不可）</Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "372px",
                height: "40px",
                "@media screen and (max-width:900px)": {
                  width: "248px",
                },
              }}
            >
              {user.m_organizations?.map((organization, index) => (
                <Chip key={index} label={organization.department_name} />
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "min-content",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              "@media screen and (max-width:600px)": {
                width: "100%",
                flexDirection: "column",
              },
            }}
          >
            <Box sx={{ width: "248px" }}>ユーザー名（変更不可）</Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "14px",
                width: "372px",
                border: "1px solid rgba(0, 0, 0, 0.6)",
                borderRadius: "4px",
                height: "40px",
                "@media screen and (max-width:900px)": {
                  width: "248px",
                },
              }}
            >
              {user.name || ""}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "min-content",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              "@media screen and (max-width:600px)": {
                width: "100%",
                flexDirection: "column",
              },
            }}
          >
            <Box sx={{ width: "248px" }}>パスワード</Box>
            <FormControl
              size="small"
              fullWidth
              variant="outlined"
              sx={{
                width: "372px",
                "@media screen and (max-width:900px)": {
                  width: "248px",
                },
              }}
            >
              <InputLabel htmlFor="outlined-adornment-password">
                パスワード
              </InputLabel>
              <OutlinedInput
                name="passwordInput"
                id="outlined-adornment-password"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                onChange={handleChangePass}
              />
            </FormControl>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              "@media screen and (max-width:600px)": {
                flexDirection: "column",
                alignItems: "center",
              },
            }}
          >
            <Button
              name="updateButton"
              variant="contained"
              sx={{ width: "208px" }}
              onClick={() => void putPostUser(true)}
            >
              更新
            </Button>
          </Box>
        </Box>
      </Box>
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
}
